/**
 * STUDIO D'AFFICHES IA — composant partagé (Événements ↔ Générateur d'affiches).
 * Idée → IA (titre/accroche/scène) → image générée (Nano Banana) ou tes photos → QR traçable.
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, Loader2, Copy, Download, ExternalLink, Printer, Upload, Wand2, X } from "lucide-react";
import { PosterPackButton } from "./PosterPackButton";

const FORMATS = [
  { v: "carre", l: "Carré (post FB/Insta 1:1)" },
  { v: "story", l: "Story / Reel (9:16)" },
  { v: "porte", l: "Affiche verticale (impression)" },
  { v: "a4", l: "Affiche murale A4/A3" },
];
export const QR_DESTS = [
  { v: "essai", l: "Réserver un cours d'essai — 19,99 $" },
  { v: "inscription", l: "Inscription en ligne" },
  { v: "formation", l: "Formation en ligne KAIZEN" },
  { v: "evenements", l: "Page des événements" },
  { v: "pkh", l: "PowerKick Her (adhésion)" },
  { v: "custom", l: "Lien personnalisé…" },
];
const FB_PAGE_URL = "https://www.facebook.com/profile.php?id=61578616646149";
const inp = "mt-1 w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500";

export default function PosterStudio({ initial = {}, eventPhotos = [], showIdea = true, compact = false }) {
  const [f, setF] = useState({
    event_title: "", event_dates: "", event_location: "Val-d'Or, Québec", subtitle: "",
    teaching: false, accent_word: "", scene: "", qr_dest: "essai", qr_custom_url: "", ...initial,
  });
  const [idea, setIdea] = useState("");
  const [ideaBusy, setIdeaBusy] = useState(false);
  const [fmt, setFmt] = useState("carre");
  const [visual, setVisual] = useState("ia"); // ia | photos
  const [refs, setRefs] = useState([]);
  const [useEventPhotos, setUseEventPhotos] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [aiCaption, setAiCaption] = useState("");

  useEffect(() => { setF((p) => ({ ...p, ...initial })); }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e });

  const body = () => ({
    ...f, format: fmt,
    reference_images: visual === "photos" ? refs : [],
    reference_photo_urls: visual === "photos" && useEventPhotos ? eventPhotos.slice(0, 4).map((p) => p.url) : [],
    accent_word: f.accent_word || undefined,
    scene: f.scene || undefined,
    qr_custom_url: f.qr_dest === "custom" ? f.qr_custom_url : undefined,
  });

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []).slice(0, 4);
    const b64s = await Promise.all(files.map((file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); })));
    setRefs(b64s); setVisual("photos");
  };

  const askIdea = async () => {
    if (!idea.trim()) return toast.error("Décris ton idée d'affiche d'abord.");
    setIdeaBusy(true);
    try {
      const { data } = await api.post("/poster/idea", { idea, event_title: f.event_title, event_dates: f.event_dates, event_location: f.event_location });
      setF({ ...f, event_title: data.title || f.event_title, subtitle: data.subtitle || f.subtitle, accent_word: data.accent_word || "", scene: data.scene || "", qr_dest: data.qr_dest || f.qr_dest });
      setAiCaption(data.caption || "");
      toast.success("Proposition IA prête — ajuste puis génère l'affiche.");
    } catch (e) { toast.error(e?.response?.data?.detail || "IA indisponible"); }
    finally { setIdeaBusy(false); }
  };

  const generate = async () => {
    if (!f.event_title.trim()) return toast.error("Le titre est requis.");
    if (f.qr_dest === "custom" && !/^https?:\/\//.test(f.qr_custom_url || "")) return toast.error("Lien personnalisé invalide (doit commencer par https://).");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/poster/event", body());
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data: job } = await api.get(`/poster/job/${data.job_id}`);
          if (job.status === "done") { setResult(job.result); setBusy(false); toast.success("Affiche générée ✓"); }
          else if (job.status === "error") { toast.error(job.error || "Échec de génération"); setBusy(false); }
          else if (tries > 40) { toast.error("Délai dépassé, réessaie"); setBusy(false); }
          else setTimeout(poll, 3000);
        } catch { setBusy(false); toast.error("Erreur de suivi de la génération"); }
      };
      setTimeout(poll, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setBusy(false); }
  };

  const caption = aiCaption || result?.caption || "";
  const copy = async (txt, msg) => { try { await navigator.clipboard.writeText(txt); toast.success(msg); } catch { toast.error("Impossible de copier"); } };
  const printPoster = () => { const w = window.open(result.full_url || result.url, "_blank"); if (w) w.addEventListener("load", () => setTimeout(() => w.print(), 300)); };

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="poster-studio">
      {showIdea && (
        <div className="mb-5 p-4 rounded-lg border border-rose-500/30 bg-rose-500/5" data-testid="poster-idea-block">
          <div className="flex items-center gap-2 text-sm font-semibold text-rose-200"><Wand2 className="w-4 h-4" /> 1. Donne ton idée — l'IA fait le reste</div>
          <textarea value={idea} onChange={(e) => setIdea(e.target.value)} rows={2} data-testid="poster-idea-input"
            placeholder="Ex. Affiche pour notre gala de fin d'année le 20 juin, ambiance prestige, mettre en valeur les ceintures noires, inviter les familles…"
            className={inp} />
          <button onClick={askIdea} disabled={ideaBusy} data-testid="poster-idea-btn"
            className="mt-2 inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-semibold">
            {ideaBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {ideaBusy ? "L'IA réfléchit…" : "Proposer titre, accroche & visuel"}
          </button>
        </div>
      )}

      <div className="text-sm font-semibold text-zinc-200 mb-2">{showIdea ? "2. " : ""}Contenu de l'affiche</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-zinc-400">Titre *<input value={f.event_title} onChange={set("event_title")} data-testid="poster-title" className={inp} placeholder="Ex. GALA IMPACT 2026" /></label>
        <label className="text-xs text-zinc-400">Mot en <span className="text-rose-400 font-semibold">rouge</span><input value={f.accent_word} onChange={set("accent_word")} data-testid="poster-accent-word" className={inp} placeholder="vide = dernier mot" /></label>
        <label className="text-xs text-zinc-400">Dates<input value={f.event_dates} onChange={set("event_dates")} data-testid="poster-dates" className={inp} placeholder="Ex. 20 juin 2026 · 18 h" /></label>
        <label className="text-xs text-zinc-400">Lieu<input value={f.event_location} onChange={set("event_location")} data-testid="poster-location" className={inp} /></label>
        <label className="text-xs text-zinc-400 sm:col-span-2">Accroche / sous-titre<input value={f.subtitle} onChange={set("subtitle")} data-testid="poster-subtitle" className={inp} placeholder="Ex. Une soirée pour célébrer nos champions" /></label>
        <label className="text-xs text-zinc-400 sm:col-span-2">Description du visuel (IA) <span className="text-zinc-500">— optionnel, rempli par « mon idée »</span>
          <textarea value={f.scene} onChange={set("scene")} rows={2} data-testid="poster-scene" className={inp} placeholder="Ex. Un combattant en kimono noir sous des projecteurs dorés, public flou en arrière-plan…" /></label>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-4 items-start">
        <label className="text-xs text-zinc-400">Format
          <select value={fmt} onChange={(e) => setFmt(e.target.value)} data-testid="poster-format" className={inp}>{FORMATS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}</select>
        </label>
        <label className="text-xs text-zinc-400">Le code QR mène vers
          <select value={f.qr_dest} onChange={set("qr_dest")} data-testid="poster-qr-dest" className={inp}>{QR_DESTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}</select>
          {f.qr_dest === "custom" && <input value={f.qr_custom_url} onChange={set("qr_custom_url")} data-testid="poster-qr-custom" placeholder="https://…" className={inp} />}
        </label>
        <label className="flex items-center gap-2 text-xs text-zinc-300 mt-5 cursor-pointer">
          <input type="checkbox" checked={!!f.teaching} onChange={set("teaching")} data-testid="poster-teaching" /> Notre instructeur y participe
        </label>
      </div>

      <div className="mt-4">
        <div className="text-sm font-semibold text-zinc-200 mb-2">{showIdea ? "3. " : ""}Visuel</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <button type="button" onClick={() => setVisual("ia")} data-testid="poster-visual-ia"
            className={`p-3 rounded-lg border text-left ${visual === "ia" ? "border-rose-500 bg-rose-500/10" : "border-zinc-700 hover:border-zinc-500"}`}>
            <div className="flex items-center gap-2 font-semibold text-sm"><Sparkles className="w-4 h-4 text-rose-400" /> L'IA génère l'image</div>
            <div className="text-xs text-zinc-400 mt-0.5">À partir de la description du visuel ci-dessus.</div>
          </button>
          <div className={`p-3 rounded-lg border ${visual === "photos" ? "border-rose-500 bg-rose-500/10" : "border-zinc-700"}`}>
            <label className="flex items-center gap-2 font-semibold text-sm cursor-pointer"><Upload className="w-4 h-4 text-amber-400" /> Mes photos (de mon ordinateur)
              <input type="file" accept="image/*" multiple onChange={(e) => onFiles(e.target.files)} data-testid="poster-refs" className="hidden" />
            </label>
            <div className="text-xs text-zinc-400 mt-0.5">Jusqu'à 4 photos — l'IA les intègre comme sujet central.</div>
            {refs.length > 0 && (
              <div className="flex gap-1.5 mt-2" data-testid="poster-refs-preview">
                {refs.map((r, i) => <img key={i} src={r} alt="" className="w-12 h-12 object-cover rounded border border-zinc-700" />)}
                <button type="button" onClick={() => { setRefs([]); setVisual("ia"); }} className="text-zinc-500 hover:text-zinc-200" title="Retirer"><X className="w-4 h-4" /></button>
              </div>
            )}
            {eventPhotos.length > 0 && (
              <label className="flex items-center gap-2 mt-2 text-xs text-zinc-300 cursor-pointer">
                <input type="checkbox" checked={useEventPhotos} onChange={(e) => { setUseEventPhotos(e.target.checked); if (e.target.checked) setVisual("photos"); }} data-testid="poster-use-event-photos" />
                Utiliser les {Math.min(4, eventPhotos.length)} photo(s) déjà stockées de cet événement
              </label>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={generate} disabled={busy} data-testid="poster-generate-btn"
          className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white px-5 py-2.5 rounded-lg text-sm font-semibold">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
          {busy ? "Génération en cours (~30-60 s)…" : "Générer l'affiche"}
        </button>
        <PosterPackButton endpoint="/poster/event/pack" getBody={body} filename="pack-affiches.zip" label="Pack 4 formats (ZIP)" testId="poster-pack-btn" disabled={busy}
          className="inline-flex items-center gap-2 border border-rose-500/50 text-rose-200 hover:bg-rose-500/10 disabled:opacity-60 px-4 py-2.5 rounded-lg text-sm font-semibold" />
      </div>

      {result && (
        <div className={`mt-5 grid ${compact ? "" : "md:grid-cols-2"} gap-5`} data-testid="poster-result">
          <img src={result.full_url || result.url} alt="Affiche" className="w-full rounded-lg border border-zinc-800" data-testid="poster-image" />
          <div className="space-y-3">
            <div>
              <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1">Légende suggérée</div>
              <textarea readOnly value={caption} rows={8} data-testid="poster-caption" className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200" />
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => copy(caption, "Légende copiée ✓")} data-testid="poster-copy-caption" className="inline-flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded-lg"><Copy className="w-4 h-4" /> Copier la légende</button>
              <a href={result.full_url || result.url} download target="_blank" rel="noopener noreferrer" data-testid="poster-download" className="inline-flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded-lg"><Download className="w-4 h-4" /> Télécharger</a>
              <button onClick={printPoster} data-testid="poster-print" className="inline-flex items-center gap-1.5 text-sm bg-[#C9A961] hover:bg-white text-black px-3 py-1.5 rounded-lg font-semibold"><Printer className="w-4 h-4" /> Imprimer / PDF</button>
              <button onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(result.full_url || result.url)}`, "_blank", "noopener")} data-testid="poster-share-fb" className="inline-flex items-center gap-1.5 text-sm bg-[#1877F2] hover:bg-[#1877F2]/90 text-white px-3 py-1.5 rounded-lg"><ExternalLink className="w-4 h-4" /> Partager sur Facebook</button>
              <a href={FB_PAGE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-200 px-2 py-1.5"><ExternalLink className="w-3.5 h-3.5" /> Page Facebook</a>
            </div>
            {result.qr_url && <div className="text-[11px] text-zinc-500 break-all">QR traçable : {result.qr_url}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
